import '../assets/css/form.css';

import { ArrowRightOutlined, LoadingOutlined } from '@ant-design/icons';
import { Input, notification, Spin } from 'antd';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { loginUser } from '../slices/authSlice';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';

const Signin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [errorMessage, setErrorMessage] = useState('');
  const { loading, error } = useSelector((state) => state.auth);

  // Get URL parameters
  const params = new URLSearchParams(window?.location?.search);
  const redirectUrl = params.get('konnectrd') || '';
  const redir = params.get('redir') || '';
  const referralCode = params.get('konnectrfc') || '';

  const [useAccountID, setUseAccountID] = useState(false);
  const antIcon = (
    <LoadingOutlined style={{ fontSize: 24, color: '#fff' }} spin />
  );

  // Set up React Hook Form for both desktop and mobile forms
  const { handleSubmit, control } = useForm({
    defaultValues: {
      accountID: '',
      loginPin: '',
      emailAddress: '',
      password: '',
    },
  });

  const { handleSubmit: handleMobileSubmit, control: controlMobileSubmit } =
    useForm({
      defaultValues: {
        accountID: '',
        loginPin: '',
        emailAddress: '',
        password: '',
      },
    });

  const openNotificationWithIcon = (type, title, message) => {
    notification[type]({
      message: title || '',
      description: message || '',
      placement: 'bottom-right',
    });
  };

  const submitMe = async (formData) => {
    setErrorMessage('');

    try {
      // Prepare sign-in data based on selected method
      const signInData = useAccountID
        ? {
            email: formData.accountID,
            password: formData.loginPin,
            app: 'web',
            device_id: '',
            device_type: '',
            role_id: 1,
          }
        : {
            email: formData.emailAddress,
            password: formData.password,
            app: 'web',
            device_id: '',
            device_type: '',
            role_id: 1,
          };

      // Use Redux action to handle login process
      const result = await dispatch(loginUser(signInData)).unwrap();

      // Show success notification
      openNotificationWithIcon(
        'success',
        'Login Successful',
        'You have successfully logged in'
      );

      // Redirect based on parameters or user role
      if (redir) {
        navigate(`/${redir}`);
      } else if (
        result?.user?.is_restaurant ||
        result?.user?.is_vendor ||
        result?.user?.is_so
      ) {
        navigate('/restaurant/dashboard');
        // navigate('/home')
      } else {
        navigate('/home');
      }
    } catch (err) {
      console.log({ err });
      // Handle specific errors
      if (err.includes && err.includes('not been verified')) {
        navigate('/verify-email');
      } else {
        setErrorMessage(err ?? 'An error occurred while signing in.');
        openNotificationWithIcon(
          'error',
          'Authentication Error',
          err || 'An error occurred while signing in. Please try again.'
        );
      }
    }
  };

  // Render form fields based on the selected auth method
  const renderAuthFields = (formControl) => {
    return useAccountID ? (
      <>
        <div className="form_group">
          <label htmlFor="accountID" style={{ width: '100%' }}>
            Account ID
          </label>
          <Controller
            name="accountID"
            defaultValue=""
            control={formControl}
            render={({ field }) => (
              <Input
                {...field}
                type="text"
                style={{ height: window.innerWidth <= 768 ? '5rem' : '4.5rem' }}
              />
            )}
          />
        </div>
        <div className="form_group">
          <label htmlFor="loginPin">Login Pin</label>
          <Controller
            name="loginPin"
            defaultValue=""
            control={formControl}
            render={({ field }) => (
              <Input.Password
                {...field}
                inputMode="numeric"
                style={{ height: window.innerWidth <= 768 ? '5rem' : '4.5rem' }}
              />
            )}
          />
        </div>
      </>
    ) : (
      <>
        <div className="form_group">
          <label htmlFor="emailAddress" style={{ width: '100%' }}>
            Email Address
          </label>
          <Controller
            name="emailAddress"
            defaultValue=""
            control={formControl}
            render={({ field }) => (
              <Input
                {...field}
                type="email"
                style={{ height: window.innerWidth <= 768 ? '5rem' : '4.5rem' }}
              />
            )}
          />
        </div>
        <div className="form_group">
          <label htmlFor="password">Password</label>
          <Controller
            name="password"
            defaultValue=""
            control={formControl}
            render={({ field }) => (
              <Input.Password
                {...field}
                type="password"
                style={{ height: window.innerWidth <= 768 ? '5rem' : '4.5rem' }}
              />
            )}
          />
        </div>
      </>
    );
  };

  return (
    <div className="form-pages">
      {/* Mobile View */}
      <div className="mobile">
        <Navbar />
        <div className="container form_design_pages">
          <div className="">
            <div className="form auth">
              <div className="form-text">
                <h3 className="text-5xl">Login to your Konnect account!</h3>
                <p className="text-3xl">
                  Think Food, think Konnect. Family & friends eat cheaper. Refer
                  friends and earn monthly.
                </p>
                {errorMessage && <p className="form-error">{errorMessage}</p>}
              </div>
              <form onSubmit={handleMobileSubmit(submitMe)}>
                <div className="flex justify-between items-center mb-4">
                  <button
                    type="button"
                    onClick={() => setUseAccountID(!useAccountID)}
                    className="text-white text-[12px] font-gilroyBold focus:outline-none py-2 px-5 rounded-xl bg-[#1f6b2b]"
                  >
                    {useAccountID
                      ? 'Sign in using Email Address'
                      : 'Sign in with Account ID'}
                    <ArrowRightOutlined style={{ marginLeft: '3px' }} />
                  </button>
                </div>

                {renderAuthFields(controlMobileSubmit)}

                <div>
                  <Link
                    style={{ display: 'block', marginBottom: 5 }}
                    to={`/signup/?konnectrfc=${referralCode}&konnectrd=${redirectUrl}`}
                  >
                    New customer? <span>Create your account</span>
                  </Link>
                  <Link style={{ display: 'block' }} to={`/resetsendmail`}>
                    {useAccountID ? 'Forgot Pin?' : 'Lost Password?'}{' '}
                    <span>
                      {useAccountID ? 'Recover pin' : 'Recover password'}
                    </span>
                  </Link>
                </div>

                <button
                  className="bg_green"
                  disabled={loading}
                  style={{
                    height: '4.5rem',
                    width: '100%',
                    borderRadius: '3px',
                    marginTop: '10%',
                  }}
                >
                  {loading ? <Spin indicator={antIcon} /> : 'Sign in'}
                </button>
              </form>
              <div style={{ marginBottom: '20px' }}></div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop View */}
      <div className="desktop show-all">
        <Navbar />
        <div className="bg_grey">
          <div className="container form_design_pages">
            <div className="div_bg_white">
              <div className="form aut">
                <div className="form-text">
                  <h3 className="noSpace">Login to your Konnect account!</h3>
                  <p>
                    Think Food, think Konnect. Family & friends eat cheaper.
                    Refer friends and earn monthly.
                  </p>
                  {errorMessage && <p className="form-error">{errorMessage}</p>}
                </div>
                <form onSubmit={handleSubmit(submitMe)}>
                  <div className="flex justify-between items-center mb-4">
                    <button
                      type="button"
                      onClick={() => setUseAccountID(!useAccountID)}
                      className="text-white text-[13px] font-gilroyBold focus:outline-none py-2 px-5 rounded-xl bg-[#1f6b2b]"
                    >
                      {useAccountID
                        ? 'Sign in using Email Address'
                        : 'Sign in with Account ID'}
                      <ArrowRightOutlined style={{ marginLeft: '3px' }} />
                    </button>
                  </div>

                  {renderAuthFields(control)}

                  <div style={{ marginTop: '2rem' }}></div>
                  <button
                    className="bg_green"
                    disabled={loading}
                    style={{
                      height: '4.5rem',
                      width: '100%',
                      borderRadius: '3px',
                    }}
                  >
                    {loading ? <Spin indicator={antIcon} /> : 'Sign in'}
                  </button>
                </form>
                <div style={{ marginBottom: '20px' }}></div>
                <div className="">
                  <Link
                    style={{
                      fontSize: '1.3rem',
                      display: 'block',
                      marginBottom: 5,
                    }}
                    to={`/signup/?konnectrfc=${referralCode}&konnectrd=${redirectUrl}`}
                  >
                    New customer? <span>Create your account</span>
                  </Link>
                  <Link
                    style={{ fontSize: '1.3rem', display: 'block' }}
                    to={`/resetsendmail`}
                  >
                    {useAccountID ? 'Forgot Pin?' : 'Lost Password?'}{' '}
                    <span>
                      {useAccountID ? 'Recover pin' : 'Recover password'}
                    </span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Signin;
